import { store } from '../store'
import { handleApiError } from '../modules/core/services/api/api-error-handler'
import { buildApiError } from './api-error'

/**
 * Server API Client
 *
 * Authenticated fetch wrapper for server admin API calls.
 * Reads the access token from Redux memory (post-cookie migration). Sends cookies
 * with every request so the server can rotate the HttpOnly refresh token cookie on
 * `/v1/authentication/*` calls.
 */

// Empty string in dev means relative paths via the Vite proxy (required for the
// HttpOnly refresh cookie to flow). Use `??` semantics to preserve the empty value.
const SERVER_API_BASE_URL =
  typeof import.meta.env.VITE_API_URL === 'string'
    ? import.meta.env.VITE_API_URL
    : 'http://localhost:3000'

export interface ServerApiOptions extends RequestInit {
  // Additional options can be added here
}

/**
 * Internal fetch function for server API
 * This is the core fetch logic that can be retried after token refresh
 */
async function performServerFetch<T = any>(
  endpoint: string,
  options: ServerApiOptions = {}
): Promise<T> {
  const token = store.getState().authenticationView.accessToken

  const headers = new Headers(options.headers || {})
  headers.set('Content-Type', 'application/json')
  headers.set('User-Agent', 'BirdBrain-Admin/1.0')
  headers.set('X-Client-Type', 'web')

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const url = `${SERVER_API_BASE_URL}${endpoint}`

  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  })

  if (!response.ok) {
    throw await buildApiError(response)
  }

  return response.json()
}

/**
 * Authenticated fetch wrapper for server admin API
 * Includes JWT token in Authorization header and automatic token refresh
 */
export async function serverApiFetch<T = any>(
  endpoint: string,
  options: ServerApiOptions = {}
): Promise<T> {
  try {
    return await performServerFetch<T>(endpoint, options)
  } catch (error: any) {
    try {
      const result = await handleApiError(error, performServerFetch, [endpoint, options], null)
      return result as T
    } catch (handledError) {
      if (error.status === 401) {
        throw new Error('Unauthorized - Please log in again')
      } else if (error.status === 403) {
        throw new Error('Forbidden - Admin access required')
      } else {
        throw error
      }
    }
  }
}

/**
 * Export the base URL for use in other parts of the app
 */
export { SERVER_API_BASE_URL }
